import { CSP_TESTSEITE } from '../../shared/csp.js';
import { modus } from '../api.js';
import { abschnitt, h, knopf, meldung, schritte, setzeMeldung } from '../dom.js';
import { melde } from '../ergebnisse.js';
import type { Ansicht } from '../router.js';
import { Aufraeumer, kopf, pruefpunktStand } from './gemeinsam.js';

/**
 * Einstieg zur CSP-Testseite. Die Testseite selbst ist ein eigenes Dokument
 * (src/web/csp.html), denn nur ein frisch geladenes Dokument bekommt ihren
 * Header; ein Seitenwechsel innerhalb der App behielte den der App.
 */
export const sicherheit: Ansicht = (ziel) => {
  const aufraeumer = new Aufraeumer();
  const statusHand = meldung();

  ziel.append(
    ...kopf(
      'CSP und Trusted Types',
      'Läuft eine Seite mit durchsetzender Content-Security-Policy und Trusted Types? Laut Fachseiten am iPhone erst ab Safari\u00a026 (Konzept, Sicherheit).',
    ),
    abschnitt('Stand', pruefpunktStand('trusted-types', aufraeumer), pruefpunktStand('csp-bericht', aufraeumer)),
    abschnitt(
      'Testseite',
      h('p', {}, 'Nur die Testseite trägt die strenge CSP mit Trusted Types; alle anderen Seiten bleiben, wie sie sind. Sie prüft sich selbst und löst absichtlich Verstöße aus, die der Browser an den Server meldet.'),
      schritte(
        '„Testseite öffnen“ tippen und warten, bis beide Prüfpunkte ein Ergebnis zeigen (bis zu 2\u00a0Minuten).',
        'Danach dieselbe Seite im Browser öffnen (iPhone: Safari, Android: Chrome), Adresse siehe unten.',
        'Bleibt die Testseite leer oder bei „Die Prüfung startet …“ stehen, hier unten „Testseite blieb stehen“ wählen.',
      ),
      // target="_self": Der Router der App lässt den Link durch, die Testseite lädt als eigenes Dokument.
      h('a', { href: CSP_TESTSEITE, target: '_self', class: 'knopf knopf-primaer' }, 'Testseite öffnen'),
      h('p', {}, 'Adresse für den Browser: ', h('span', { class: 'code', translate: 'no' }, `${location.host}${CSP_TESTSEITE}`)),
    ),
    abschnitt(
      'Wenn die Testseite stehen bleibt',
      h(
        'div',
        { class: 'frage', role: 'group', 'aria-labelledby': 'frage-csp' },
        h('p', { id: 'frage-csp' }, 'Blieb die Testseite leer oder bei „Die Prüfung startet …“ stehen?'),
        knopf(
          'Testseite blieb stehen',
          async () => {
            await melde('trusted-types', 'fehler', 'Die Testseite blieb stehen: Ihr Skript lief unter der strengen CSP nicht (laut Angabe).', { quelle: 'von Hand', modus: modus() });
            setzeMeldung(statusHand, 'Gespeichert.', 'ok');
          },
          'gefahr',
        ),
      ),
      statusHand,
    ),
  );
  return aufraeumer.alles;
};

/**
 * Die App zeigt /csp-test nur, wenn die echte Testseite nicht geladen wurde: nach
 * einem Seitenwechsel innerhalb der App oder unter einer Schreibweise, die der
 * Server nicht kennt (dann die Testseite laden), oder ohne Netz, wenn der Service
 * Worker die gespeicherte App liefert (dann nicht, sonst entstünde eine Schleife).
 */
export const cspTestOhneSeite: Ansicht = (ziel) => {
  const geladen = performance.getEntriesByType('navigation')[0]?.name;
  if (location.pathname !== CSP_TESTSEITE || !geladen || new URL(geladen).pathname !== CSP_TESTSEITE) {
    location.replace(`${CSP_TESTSEITE}${location.search}`);
    return;
  }
  ziel.append(
    ...kopf('CSP und Trusted Types'),
    h('p', { class: 'meldung meldung-fehler' }, 'Die Testseite braucht eine Verbindung zum Server. Netz prüfen und erneut laden.'),
    knopf('Erneut laden', () => location.reload()),
    h('p', {}, h('a', { href: '/pruefung/sicherheit' }, 'Zurück zur Prüfung')),
  );
};
